// Formatting utilities
// State-dependent functions access centralized store

import { store } from '../store.js'

export function formatDateWithFormat(d, formato) {
    if (!d) return 'Sin fecha';

    let year, month, day;
    if (typeof d === 'string' && d.match(/^\d{4}-\d{2}-\d{2}/)) {
        [year, month, day] = d.substring(0, 10).split('-').map(Number);
    } else {
        const dt = new Date(d);
        if (isNaN(dt)) return String(d).substring(0, 15);
        year = dt.getFullYear();
        month = dt.getMonth() + 1;
        day = dt.getDate();
    }

    const mesesCortos = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const mesesLargos = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const mesesCortosMin = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

    switch (formato) {
        case 'mes_dia_año':
            return `${mesesCortos[month - 1]} ${day}, ${year}`;
        case 'mes_largo_dia_año':
            return `${mesesLargos[month - 1]} ${day}, ${year}`;
        case 'dia_mes_año':
            return `${day} de ${mesesCortosMin[month - 1]} de ${year}`;
        case 'dia_mes_largo_año':
            return `${day} de ${mesesLargos[month - 1].toLowerCase()} de ${year}`;
        case 'numerico_dmy':
            return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
        case 'numerico_mdy':
            return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
        default:
            return `${mesesCortos[month - 1]} ${day}, ${year}`;
    }
}

export const normalizePhone = (p) => {
    if (!p) return null;
    // Quitar todo excepto números
    let d = String(p).replace(/\D/g, '');
    if (d.length < 7) return null;

    // Si ya tiene código de país largo (10+ dígitos), agregar +
    if (d.length >= 10) {
        // Colombia: 57 + 10 dígitos = 12
        if (d.startsWith('57') && d.length === 12) return '+' + d;
        // USA/Canadá: 1 + 10 dígitos = 11
        if (d.startsWith('1') && d.length === 11) return '+' + d;
        // México: 52 + 10 dígitos = 12
        if (d.startsWith('52') && d.length === 12) return '+' + d;
        // Si empieza con 3 y tiene 10 dígitos, asumir Colombia
        if (d.startsWith('3') && d.length === 10) return '+57' + d;
        // Si tiene 10 dígitos sin código claro, asumir Colombia
        if (d.length === 10) return '+57' + d;
        // Si tiene 11+ dígitos pero no coincide con patrones conocidos, agregar + y confiar
        if (d.length >= 11) return '+' + d;
    }

    // Número corto, guardar sin +
    return d;
};

export const formatPhoneDisplay = (p) => {
    if (!p) return '';
    const clean = String(p).replace(/\D/g, '');
    if (clean.length === 12 && clean.startsWith('57')) {
        return `+57 ${clean.slice(2, 5)} ${clean.slice(5, 8)} ${clean.slice(8)}`;
    }
    if (clean.length === 11 && clean.startsWith('1')) {
        return `+1 ${clean.slice(1, 4)} ${clean.slice(4, 7)} ${clean.slice(7)}`;
    }
    return p.startsWith('+') ? p : '+' + clean;
};

export const parseClientName = (n) => {
    if (!n) return { titular: null, contacto: null };
    const m = String(n).trim().match(/^(.+?)\s*\((.+?)\)\s*$/);
    return m ? { titular: m[1].trim(), contacto: m[2].trim() } : { titular: String(n).trim(), contacto: null };
};

// Strip currency symbols and locale formatting, return a clean float
export const cleanNum = (str) => {
    let s = String(str || '0').replace(/[$\s]/g, '')
    if (store.numberFormat === 'es-CO') {
        s = s.replace(/\./g, '').replace(',', '.')  // 2.471,01 → 2471.01
    } else {
        s = s.replace(/,/g, '')  // 2,471.01 → 2471.01
    }
    return parseFloat(s) || 0
}

export const fmt = (n) => {
    if (store?.ocultarValores) return '•••';
    const locale = store.numberFormat === 'es-CO' ? 'es-CO' : 'en-US';
    return !n || isNaN(n) ? '$0.00' : '$' + Number(n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const normalizeName = (name) => name ? String(name).trim().replace(/\s+/g, ' ') : '';

export const fmtDate = (d) => formatDateWithFormat(d, store?.settings?.formatoFecha || 'mes_dia_año');

export function fmtViajeros(l, mode) {
    const fd = l.formData || {};
    const a = parseInt(fd.adultos) || 0;
    const m = parseInt(fd.menores) || 0;
    const rawAges = fd.edadesMenores || [];
    const ages = Array.isArray(rawAges) ? rawAges : (typeof rawAges === 'string' && rawAges.trim() ? rawAges.split(',').map(s => s.trim()) : []);
    if (a === 0 && m === 0) return l.viajeros || null;
    if (mode === 'card') {
        if (m === 0) return `${a}A`;
        return `${a}A + ${m}M` + (ages.length ? ` (${ages.join(', ')})` : '');
    }
    // panel mode
    const aPart = a === 1 ? '1 adulto' : `${a} adultos`;
    if (m === 0) return aPart;
    const mPart = m === 1 ? '1 menor' : `${m} menores`;
    const ageStr = ages.length ? ` (${ages.join(', ')} años)` : '';
    return `${aPart}, ${mPart}${ageStr}`;
}

export const getMonthKey = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d)) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const getMonthLabel = (key) => {
    if (!key) return '';
    const [year, month] = key.split('-');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${months[parseInt(month) - 1]} ${year}`;
};

export function escHtml(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function normalizeForCompare(name) {
    return (name || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
}

export function fmtUSD(n) {
    if (store?.ocultarValores) return '$•••';
    const locale = store.numberFormat === 'es-CO' ? 'es-CO' : 'en-US';
    return '$' + (parseFloat(n) || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtDateTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    const day = d.getDate();
    const month = d.toLocaleDateString('es', { month: 'short' });
    const hours = d.getHours().toString().padStart(2, '0');
    const mins = d.getMinutes().toString().padStart(2, '0');
    return `${day} ${month} ${hours}:${mins}`;
}

export function fmtHabitaciones(fd) {
    if (!fd) return '';
    if (fd.mismaHabitacion) return '1 (quieren acomodarse juntos)';
    if (fd.distribucionHabitaciones?.length > 0) {
        return fd.distribucionHabitaciones.map((r, i) => {
            let parts = `${r.adultos}A`;
            if (r.menores > 0) {
                const ages = (r.edadesMenores || []).filter(a => a != null);
                parts += ` + ${r.menores}M` + (ages.length > 0 ? ` (${ages.join(', ')})` : '');
            }
            return `Hab ${i + 1}: ${parts}`;
        }).join(' | ');
    }
    return fd.habitaciones || '';
}

// Register on window for inline module access
Object.assign(window, {
    formatDateWithFormat, normalizePhone, formatPhoneDisplay, parseClientName,
    fmt, normalizeName, fmtDate, fmtViajeros, getMonthKey, getMonthLabel,
    escHtml, normalizeForCompare, fmtUSD, fmtDateTime, cleanNum, fmtHabitaciones
});
